/**
 * Godot-style signals: connect / disconnect / emit / is_connected.
 */

export type SignalCallable = (...args: unknown[]) => void;

export interface SignalConnection {
  callable: SignalCallable;
  once: boolean;
  /** Optional owner id for auto-disconnect on free */
  ownerId?: string;
}

export class Signal {
  readonly name: string;
  private connections: SignalConnection[] = [];

  constructor(name: string) {
    this.name = name;
  }

  connect(callable: SignalCallable, opts?: { once?: boolean; ownerId?: string }): void {
    if (this.is_connected(callable)) return;
    this.connections.push({
      callable,
      once: !!opts?.once,
      ownerId: opts?.ownerId,
    });
  }

  disconnect(callable: SignalCallable): void {
    this.connections = this.connections.filter((c) => c.callable !== callable);
  }

  is_connected(callable: SignalCallable): boolean {
    return this.connections.some((c) => c.callable === callable);
  }

  get_connections(): SignalConnection[] {
    return this.connections.slice();
  }

  emit(...args: unknown[]): void {
    const list = this.connections.slice();
    for (const c of list) {
      try {
        c.callable(...args);
      } catch (e) {
        console.error(`[signal ${this.name}]`, e);
      }
      if (c.once) this.disconnect(c.callable);
    }
  }

  /** Remove all connections owned by a node id (queue_free cleanup). */
  disconnect_owner(ownerId: string): void {
    this.connections = this.connections.filter((c) => c.ownerId !== ownerId);
  }

  clear(): void {
    this.connections = [];
  }
}

/** Per-node signal bus keyed by signal name. */
export class SignalBus {
  private map = new Map<string, Signal>();

  signal(name: string): Signal {
    let s = this.map.get(name);
    if (!s) {
      s = new Signal(name);
      this.map.set(name, s);
    }
    return s;
  }

  has(name: string): boolean {
    return this.map.has(name);
  }

  names(): string[] {
    return Array.from(this.map.keys());
  }

  disconnect_owner(ownerId: string): void {
    for (const s of this.map.values()) s.disconnect_owner(ownerId);
  }

  clear(): void {
    for (const s of this.map.values()) s.clear();
    this.map.clear();
  }
}
